import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useRef } from 'react'
import { ArrowLeft, ClipboardList, Send, Paperclip, X } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DatePicker } from '@/components/ui/date-picker'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreateTicket, useUsers, useUploadContent, useAccounts } from '@/hooks/use-api'
import { useDebounce } from '@/hooks/use-debounce'
import { RecordCombobox } from '@/components/ui/record-combobox'
import { useToast } from '@/hooks/use-toast'
import { useUser } from '@/contexts/UserContext'
import type { TicketPriority } from '@/types'
import { CATEGORICAL } from '@/lib/theme'
import { explainError } from '@/lib/explain-error'

const REQUEST_TYPES = [
  'Request System Access',
  'New Employee Onboarding',
  'Feature Request',
  'Report a Bug',
  'Request a Change',
  'Dashboard Account',
  'Security Questionnaires',
  'Trial Request',
  'Marketing Feedback / Suggestion',
  'GPO Client Feedback',
  'Scan Request',
  'Trial Scan',
  'Shipping Label',
] as const

type RequestType = (typeof REQUEST_TYPES)[number]

// Request types that have a "Company Name" field — selecting a Related Account
// auto-fills Company Name for these so it doesn't have to be retyped.
const COMPANY_NAME_REQUEST_TYPES = new Set<RequestType>([
  'Dashboard Account',
  'Security Questionnaires',
  'Trial Request',
  'Scan Request',
  'Trial Scan',
])

const SYSTEM_OPTIONS = ['GRM', 'Pipedrive', 'Google Drive', 'Slack', 'GitHub', 'Jira', 'Other']

const AFFECTED_SERVICES = [
  'GRM',
  'Website',
  'Checker',
  'Chrome Extension',
  'Google Workspace Add-on',
  'API',
  'Other',
]

const URGENCY_OPTIONS = ['Low', 'Medium', 'High', 'Critical']

const CHANGE_TYPE_OPTIONS = ['Process', 'Software', 'Hardware', 'Policy']

const IMPACT_OPTIONS = ['Low', 'Medium', 'High']

const CLIENT_TYPE_OPTIONS = ['New', 'Existing']

// Trial Request's Product Type. Kept next to its sibling so the two PDF products read
// together rather than the newer one trailing at the end of the list.
const PRODUCT_TYPE_OPTIONS = [
  'Grackle PDF',
  'Grackle PDF Desktop',
  'Grackle Workspace',
  'Grackle Scan',
  'Grackle Office',
]

const TRIAL_TERM_OPTIONS = ['14 days', '30 days', '60 days', '90 days', 'Other']

const SERVICES_OPTIONS = [
  'Grackle Docs',
  'Grackle Slides',
  'Grackle Sheets',
  'Grackle Suite',
  'PDF Checker',
  'Website Checker',
  'Consulting',
  'Other',
]

function urgencyToPriority(urgency: string): string {
  switch (urgency) {
    case 'Critical':
      return 'Urgent'
    case 'High':
      return 'High'
    case 'Low':
      return 'Low'
    default:
      return 'Medium'
  }
}

export default function SupportRequest() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const { currentUser } = useUser()
  const createMutation = useCreateTicket()
  const uploadMutation = useUploadContent()
  const { data: usersData } = useUsers({ limit: 10000 })
  const users = usersData?.data || []
  const fileInputRef = useRef<HTMLInputElement>(null)
  // Per-key hidden file inputs for labelled single-file (PDF) fields.
  const pdfInputRefs = useRef<Record<string, HTMLInputElement | null>>({})

  // Optional deep-link prefill (e.g. "Request Trial Access" from an Account):
  // /support-request?type=Trial Request&account_id=..&company_name=..&sales_rep=..&contact=..&contact_id=..
  const [searchParams] = useSearchParams()

  const [requestType, setRequestType] = useState<RequestType | ''>(() => {
    const t = searchParams.get('type')
    return t && (REQUEST_TYPES as readonly string[]).includes(t) ? (t as RequestType) : ''
  })
  const [fields, setFields] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {}
    const companyName = searchParams.get('company_name')
    const salesRep = searchParams.get('sales_rep')
    const contact = searchParams.get('contact')
    const contactEmail = searchParams.get('contact_email')
    const contactPhone = searchParams.get('contact_phone')
    if (companyName) seed.company_name = companyName
    if (salesRep) seed.sales_rep = salesRep
    // `contact`/`contact_email`/`contact_phone` may have no visible input on some
    // request types, but buildDescription() renders any populated field, so they
    // land in the ticket description (e.g. "Contact Phone: …") — this is how a
    // Lead's name/phone/email carry over when raising a ticket from the lead.
    if (contact) seed.contact = contact
    if (contactEmail) seed.contact_email = contactEmail
    if (contactPhone) seed.contact_phone = contactPhone
    return seed
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [attachments, setAttachments] = useState<File[]>([])
  // Labelled single-file attachments keyed by field (e.g. input_pdf / exported_pdf),
  // so they can be uploaded with a distinguishing title. The shared `attachments`
  // array can't tell one PDF from another.
  const [namedFiles, setNamedFiles] = useState<Record<string, File>>({})
  // Contact record to link on the created ticket (from the deep-link), if any.
  const [prefillContactId] = useState(() => searchParams.get('contact_id') || '')
  // Lead to link on the created ticket (from the deep-link "Create Support Ticket"
  // action on a Lead). Unlike contact_id, this is linked for every request type.
  const [prefillLeadId] = useState(() => searchParams.get('lead_id') || '')

  // Optional account link — visible for every request type. Search-backed.
  const [accountId, setAccountId] = useState(() => searchParams.get('account_id') || '')
  const [accountSearch, setAccountSearch] = useState(() => searchParams.get('company_name') || '')
  const debouncedAccountSearch = useDebounce(accountSearch, 300)
  const { data: accountsData, isFetching: accountsLoading } = useAccounts({
    search: debouncedAccountSearch || undefined,
    limit: 10000,
    sort_by: 'name',
    sort_order: 'asc',
  })
  const accounts = accountsData?.data || []

  // Selecting a Related Account auto-fills Company Name (for request types that have it),
  // so the user doesn't have to leave the form to copy the account name.
  const handleAccountChange = (value: string) => {
    setAccountId(value)
    if (value && requestType && COMPANY_NAME_REQUEST_TYPES.has(requestType)) {
      const account = accounts.find((a) => a.id === value)
      const accountName = account?.name
      if (accountName) {
        setFields((prev) => ({ ...prev, company_name: accountName }))
        if (formErrors.company_name) {
          setFormErrors((prev) => {
            const next = { ...prev }
            delete next.company_name
            return next
          })
        }
      }
    }
  }

  const handleFieldChange = (field: string, value: string) => {
    setFields((prev) => ({ ...prev, [field]: value }))
    if (formErrors[field]) {
      setFormErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleRequestTypeChange = (value: string) => {
    const newType = value as RequestType
    setRequestType(newType)
    // Reset per-type fields, but if an account is already linked and the new type has a
    // Company Name field, re-seed it from that account so switching types doesn't force
    // a re-type. (accountId is deliberately preserved — see note below.)
    const seed: Record<string, string> = {}
    if (accountId && COMPANY_NAME_REQUEST_TYPES.has(newType)) {
      const accountName = accounts.find((a) => a.id === accountId)?.name
      if (accountName) seed.company_name = accountName
    }
    setFields(seed)
    setFormErrors({})
    setAttachments([])
    setNamedFiles({})
    // Note: deliberately NOT clearing accountId — a user who sets an account
    // then changes request type shouldn't have to re-pick the account.
  }

  const getRequiredFields = (): string[] => {
    switch (requestType) {
      case 'Request System Access':
        return ['summary', 'system', 'employee_name', 'employee_email']
      case 'New Employee Onboarding':
        return ['summary', 'first_name', 'last_name', 'role', 'department', 'start_date']
      case 'Feature Request':
        return ['summary', 'why_build']
      case 'Report a Bug':
        return ['summary', 'details', 'affected_services']
      case 'Request a Change':
        return ['summary', 'description', 'change_type']
      case 'Dashboard Account':
        return ['sales_rep', 'company_name', 'client_type']
      case 'Security Questionnaires':
        return ['company_name', 'contact']
      case 'Trial Request':
        return ['sales_rep', 'company_name', 'product_type', 'contact', 'contact_email']
      case 'Marketing Feedback / Suggestion':
        return ['requestor_name', 'feedback']
      case 'GPO Client Feedback':
        return ['summary', 'issue_description', 'workflow_description']
      case 'Scan Request':
        return ['company_name', 'domain_url']
      case 'Trial Scan':
        return ['sales_rep', 'company_name', 'contact', 'contact_email', 'domain_url']
      case 'Shipping Label':
        return ['recipient_name', 'shipping_address']
      default:
        return []
    }
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}
    if (!requestType) {
      errors.requestType = 'Please select a request type'
    }
    for (const field of getRequiredFields()) {
      if (!fields[field]?.trim()) {
        errors[field] = 'This field is required'
      }
    }
    // Validate email fields
    if (fields.employee_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.employee_email)) {
      errors.employee_email = 'Please enter a valid email address'
    }
    if (fields.requestor_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.requestor_email)) {
      errors.requestor_email = 'Please enter a valid email address'
    }
    if (fields.personal_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.personal_email)) {
      errors.personal_email = 'Please enter a valid email address'
    }
    if (fields.contact_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.contact_email)) {
      errors.contact_email = 'Please enter a valid email address'
    }
    // Trial date range: end must be on or after start when both are provided
    if (
      fields.trial_start_date &&
      fields.trial_end_date &&
      fields.trial_end_date < fields.trial_start_date
    ) {
      errors.trial_end_date = 'End date must be on or after the start date'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const buildDescription = (): string => {
    const lines: string[] = [`Request Type: ${requestType}`, '']
    const fieldLabels: Record<string, string> = {
      summary: 'Summary',
      system: 'System',
      employee_name: 'Employee Name',
      employee_email: 'Employee Email',
      reason: 'Reason',
      first_name: 'First Name',
      last_name: 'Last Name',
      role: 'Role',
      department: 'Department',
      manager: 'Manager',
      start_date: 'Start Date',
      personal_email: 'Personal Email',
      requirements: 'Requirements',
      why_build: 'Why Build This',
      client_info: 'Client Info',
      impact: 'Impact',
      notes: 'Notes',
      details: 'Details',
      affected_services: 'Affected Services',
      urgency: 'Urgency',
      description: 'Description',
      change_type: 'Change Type',
      sales_rep: 'Sales Rep',
      requestor: 'Requestor',
      requestor_email: 'Requestor Email',
      client_type: 'Client Type',
      company_name: 'Company Name',
      billing_phone: 'Billing Phone',
      address: 'Address',
      contact: 'Contact',
      contact_email: 'Contact Email',
      contact_phone: 'Contact Phone',
      services: 'Services',
      product_type: 'Product Type',
      trial_term: 'Trial Term',
      trial_start_date: 'Trial Start Date',
      trial_end_date: 'Trial End Date',
      trial_notes: 'Notes',
      scan_notes: 'Notes',
      requestor_name: 'Requestor Name',
      feedback: 'Feedback',
      domain_url: 'Domain URL',
      due_date: 'Due Date',
      recipient_name: 'Recipient Name',
      shipping_address: 'Shipping Address',
      package_weight: 'Package Weight',
      issue_description: 'Issue Description',
      workflow_description: 'Workflow Taken',
      editor_url: 'Editor URL',
    }

    for (const [key, value] of Object.entries(fields)) {
      if (value?.trim()) {
        // For sales_rep, resolve the user name
        if (key === 'sales_rep') {
          const user = users.find((u) => u.id === value)
          lines.push(`${fieldLabels[key] || key}: ${user?.name || value}`)
        } else {
          lines.push(`${fieldLabels[key] || key}: ${value}`)
        }
      }
    }
    // Record attached PDFs by filename so they're referenced in the ticket text too.
    if (namedFiles.input_pdf) lines.push(`Input PDF: ${namedFiles.input_pdf.name}`)
    if (namedFiles.exported_pdf) lines.push(`Exported PDF: ${namedFiles.exported_pdf.name}`)
    return lines.join('\n')
  }

  const getSubject = (): string => {
    const summary = fields.summary || fields.company_name || fields.requestor_name || requestType
    return `[${requestType}] ${summary}`
  }

  const getPriority = (): string => {
    if ((requestType === 'Report a Bug' || requestType === 'Request a Change') && fields.urgency) {
      return urgencyToPriority(fields.urgency)
    }
    return 'Medium'
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive',
      })
      return
    }

    try {
      const ticket = await createMutation.mutateAsync({
        subject: getSubject(),
        description: buildDescription(),
        channel: 'web',
        status: 'New',
        priority: getPriority() as TicketPriority,
        owner_id: currentUser?.id || '',
        category: requestType,
        account_id: accountId || undefined,
        // Only link the deep-link contact on a Trial Request — if the user switched
        // request type (or reset), the prefilled contact no longer applies.
        contact_id:
          requestType === 'Trial Request' && prefillContactId ? prefillContactId : undefined,
        // A ticket raised from a Lead stays linked to that lead regardless of the
        // chosen request type.
        lead_id: prefillLeadId || undefined,
      })

      // Upload attachments linked to the new ticket
      if (attachments.length > 0) {
        for (const file of attachments) {
          const formData = new FormData()
          formData.append('file', file)
          formData.append('title', file.name)
          formData.append('linked_entity_id', ticket.id)
          try {
            await uploadMutation.mutateAsync(formData)
          } catch {
            // Continue even if an attachment fails — ticket is already created
          }
        }
      }

      // Upload labelled single-file attachments (e.g. GPO Client Feedback PDFs) with a
      // distinguishing title so they're identifiable on the ticket.
      const namedTitles: Record<string, string> = {
        input_pdf: 'Input PDF',
        exported_pdf: 'Exported PDF',
      }
      for (const [key, file] of Object.entries(namedFiles)) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('title', `${namedTitles[key] || key} — ${file.name}`)
        formData.append('linked_entity_id', ticket.id)
        try {
          await uploadMutation.mutateAsync(formData)
        } catch {
          // Continue even if an attachment fails — ticket is already created
        }
      }

      toast({
        title: 'Request Submitted',
        description: `GRK-${ticket.ticket_number} has been created for your request.`,
      })
      navigate(`/tickets/${ticket.id}`)
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to submit request'),
        variant: 'destructive',
      })
    }
  }

  const renderFieldError = (field: string) => {
    if (!formErrors[field]) return null
    return (
      <p id={`${field}-error`} className="text-sm text-destructive mt-1" role="alert">
        {formErrors[field]}
      </p>
    )
  }

  const renderTextField = (field: string, label: string, required = false, placeholder = '') => (
    <div className="space-y-2">
      <Label htmlFor={field}>
        {label}
        {required ? ' *' : ''}
      </Label>
      <Input
        id={field}
        value={fields[field] || ''}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        placeholder={placeholder}
        aria-required={required || undefined}
        aria-invalid={!!formErrors[field] || undefined}
        aria-describedby={formErrors[field] ? `${field}-error` : undefined}
      />
      {renderFieldError(field)}
    </div>
  )

  const renderTextareaField = (
    field: string,
    label: string,
    required = false,
    placeholder = ''
  ) => (
    <div className="space-y-2">
      <Label htmlFor={field}>
        {label}
        {required ? ' *' : ''}
      </Label>
      <Textarea
        id={field}
        value={fields[field] || ''}
        onChange={(e) => handleFieldChange(field, e.target.value)}
        placeholder={placeholder}
        rows={4}
        aria-required={required || undefined}
        aria-invalid={!!formErrors[field] || undefined}
        aria-describedby={formErrors[field] ? `${field}-error` : undefined}
      />
      {renderFieldError(field)}
    </div>
  )

  const renderSelectField = (
    field: string,
    label: string,
    options: string[],
    required = false,
    placeholder = 'Select...'
  ) => (
    <div className="space-y-2">
      <Label htmlFor={field}>
        {label}
        {required ? ' *' : ''}
      </Label>
      <Select
        value={fields[field] || ''}
        onValueChange={(value) => handleFieldChange(field, value)}
      >
        <SelectTrigger
          id={field}
          aria-required={required || undefined}
          aria-invalid={!!formErrors[field] || undefined}
          aria-describedby={formErrors[field] ? `${field}-error` : undefined}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {renderFieldError(field)}
    </div>
  )

  const renderUserSelectField = (field: string, label: string, required = false) => (
    <div className="space-y-2">
      <Label htmlFor={field}>
        {label}
        {required ? ' *' : ''}
      </Label>
      <Select
        value={fields[field] || ''}
        onValueChange={(value) => handleFieldChange(field, value)}
      >
        <SelectTrigger
          id={field}
          aria-required={required || undefined}
          aria-invalid={!!formErrors[field] || undefined}
          aria-describedby={formErrors[field] ? `${field}-error` : undefined}
        >
          <SelectValue placeholder="Select a sales rep..." />
        </SelectTrigger>
        <SelectContent>
          {users.map((user) => (
            <SelectItem key={user.id} value={user.id}>
              {user.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {renderFieldError(field)}
    </div>
  )

  const renderDateField = (field: string, label: string, required = false) => (
    <div className="space-y-2">
      <Label htmlFor={field}>
        {label}
        {required ? ' *' : ''}
      </Label>
      <DatePicker
        id={field}
        value={fields[field]}
        onChange={(v) => handleFieldChange(field, v ?? '')}
        aria-required={required || undefined}
        aria-invalid={!!formErrors[field] || undefined}
        aria-describedby={formErrors[field] ? `${field}-error` : undefined}
      />
      {renderFieldError(field)}
    </div>
  )

  const renderAttachmentField = () => (
    <div className="space-y-2">
      <Label htmlFor="attachments">Attachments</Label>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          aria-label="Attach files"
        >
          <Paperclip className="mr-2 h-4 w-4" aria-hidden="true" />
          Attach Files
        </Button>
        <span className="text-sm text-muted-foreground">
          PNG, JPG, GIF, PDF, TXT, ZIP (max 50MB each)
        </span>
      </div>
      <input
        ref={fileInputRef}
        id="attachments"
        type="file"
        multiple
        accept=".png,.jpg,.jpeg,.gif,.pdf,.txt,.zip,.doc,.docx,.xls,.xlsx"
        className="sr-only"
        onChange={(e) => {
          if (e.target.files) {
            setAttachments((prev) => [...prev, ...Array.from(e.target.files!)])
          }
          e.target.value = ''
        }}
        aria-label="Select files to attach"
      />
      {attachments.length > 0 && (
        <ul className="space-y-1" aria-label="Selected attachments">
          {attachments.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 text-sm rounded-md bg-muted px-3 py-1.5"
            >
              <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate flex-1">{file.name}</span>
              <span className="text-muted-foreground shrink-0">
                {(file.size / 1024).toFixed(0)} KB
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 shrink-0"
                onClick={() => setAttachments((prev) => prev.filter((_, i) => i !== index))}
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )

  const renderPdfField = (fieldKey: string, label: string, required = false, help = '') => {
    const file = namedFiles[fieldKey]
    return (
      <div className="space-y-2">
        <Label htmlFor={fieldKey}>
          {label}
          {required ? ' *' : ''}
        </Label>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => pdfInputRefs.current[fieldKey]?.click()}
            aria-label={`Attach ${label}`}
          >
            <Paperclip className="mr-2 h-4 w-4" aria-hidden="true" />
            {file ? 'Replace PDF' : 'Attach PDF'}
          </Button>
          {help && <span className="text-sm text-muted-foreground">{help}</span>}
        </div>
        <input
          ref={(el) => {
            pdfInputRefs.current[fieldKey] = el
          }}
          id={fieldKey}
          type="file"
          accept=".pdf"
          className="sr-only"
          aria-required={required || undefined}
          aria-invalid={!!formErrors[fieldKey] || undefined}
          aria-describedby={formErrors[fieldKey] ? `${fieldKey}-error` : undefined}
          onChange={(e) => {
            const selected = e.target.files?.[0]
            if (selected) {
              setNamedFiles((prev) => ({ ...prev, [fieldKey]: selected }))
              setFormErrors((prev) => {
                const next = { ...prev }
                delete next[fieldKey]
                return next
              })
            }
            e.target.value = ''
          }}
          aria-label={`Select ${label}`}
        />
        {file && (
          <ul className="space-y-1" aria-label={`Selected ${label}`}>
            <li className="flex items-center gap-2 text-sm rounded-md bg-muted px-3 py-1.5">
              <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />
              <span className="truncate flex-1">{file.name}</span>
              <span className="text-muted-foreground shrink-0">
                {(file.size / 1024).toFixed(0)} KB
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-5 w-5 p-0 shrink-0"
                onClick={() =>
                  setNamedFiles((prev) => {
                    const next = { ...prev }
                    delete next[fieldKey]
                    return next
                  })
                }
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </Button>
            </li>
          </ul>
        )}
        {renderFieldError(fieldKey)}
      </div>
    )
  }

  const renderConditionalFields = () => {
    switch (requestType) {
      case 'Request System Access':
        return (
          <>
            {renderTextField('summary', 'Summary', true, 'Brief summary of the access request')}
            {renderSelectField('system', 'System', SYSTEM_OPTIONS, true, 'Select system...')}
            {renderTextField('employee_name', 'Employee Name', true)}
            {renderTextField('employee_email', 'Employee Email', true, 'employee@grackledocs.com')}
            {renderTextareaField('reason', 'Reason', false, 'Why is this access needed?')}
          </>
        )

      case 'New Employee Onboarding':
        return (
          <>
            {renderTextField('summary', 'Summary', true, 'Brief summary of onboarding request')}
            <div className="grid grid-cols-2 gap-4">
              {renderTextField('first_name', 'First Name', true)}
              {renderTextField('last_name', 'Last Name', true)}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {renderTextField('role', 'Role', true)}
              {renderTextField('department', 'Department', true)}
            </div>
            <div className="grid grid-cols-2 gap-4">
              {renderTextField('manager', 'Manager')}
              {renderDateField('start_date', 'Start Date', true)}
            </div>
            {renderTextField('personal_email', 'Personal Email', false, 'personal@email.com')}
            {renderTextareaField(
              'requirements',
              'Requirements',
              false,
              'Any special requirements for onboarding...'
            )}
          </>
        )

      case 'Feature Request':
        return (
          <>
            {renderTextField('summary', 'Summary', true, 'Brief description of the feature')}
            {renderTextareaField(
              'why_build',
              'Why Should We Build This?',
              true,
              'Explain the business case...'
            )}
            {renderTextField(
              'client_info',
              'Client Info',
              false,
              'Which clients have requested this?'
            )}
            {renderSelectField('impact', 'Impact', IMPACT_OPTIONS)}
            {renderTextareaField('notes', 'Additional Notes', false)}
          </>
        )

      case 'Report a Bug':
        return (
          <>
            {renderTextField('summary', 'Summary', true, 'Brief description of the bug')}
            {renderTextareaField(
              'details',
              'Details',
              true,
              'Steps to reproduce, expected vs actual behavior...'
            )}
            {renderSelectField(
              'affected_services',
              'Affected Services',
              AFFECTED_SERVICES,
              true,
              'Select service...'
            )}
            {renderSelectField('urgency', 'Urgency', URGENCY_OPTIONS, false, 'Select urgency...')}
            {renderTextareaField(
              'impact',
              'Impact',
              false,
              'How does this affect users or business?'
            )}
            {renderAttachmentField()}
          </>
        )

      case 'Request a Change':
        return (
          <>
            {renderTextField('summary', 'Summary', true, 'Brief description of the change')}
            {renderTextareaField(
              'description',
              'Description',
              true,
              'Detailed description of the change...'
            )}
            {renderSelectField('urgency', 'Urgency', URGENCY_OPTIONS, false, 'Select urgency...')}
            {renderSelectField(
              'change_type',
              'Change Type',
              CHANGE_TYPE_OPTIONS,
              true,
              'Select type...'
            )}
            {renderTextareaField('impact', 'Impact', false, 'What is the impact of this change?')}
            {renderTextareaField('reason', 'Reason', false, 'Why is this change needed?')}
          </>
        )

      case 'Dashboard Account':
        return (
          <>
            {renderUserSelectField('sales_rep', 'Sales Rep', true)}
            <div className="grid grid-cols-2 gap-4">
              {renderTextField('requestor', 'Requestor')}
              {renderTextField('requestor_email', 'Requestor Email', false, 'email@example.com')}
            </div>
            {renderSelectField(
              'client_type',
              'Client Type',
              CLIENT_TYPE_OPTIONS,
              true,
              'Select type...'
            )}
            {renderTextField('company_name', 'Company Name', true)}
            {renderTextField('billing_phone', 'Billing Phone')}
            {renderTextareaField('address', 'Address', false, 'Full billing address...')}
          </>
        )

      case 'Security Questionnaires':
        return (
          <>
            {renderTextField('company_name', 'Company Name', true)}
            {renderTextField('contact', 'Contact', true, 'Contact person for the questionnaire')}
            {renderSelectField(
              'services',
              'Services',
              SERVICES_OPTIONS,
              false,
              'Select service...'
            )}
            {renderUserSelectField('sales_rep', 'Sales Rep')}
            {renderDateField('due_date', 'Due Date')}
            {renderTextareaField('notes', 'Note', false, 'Notes about this questionnaire...')}
            {renderAttachmentField()}
          </>
        )

      case 'Trial Request':
        return (
          <>
            {renderUserSelectField('sales_rep', 'Sales Rep', true)}
            {renderTextField('company_name', 'Company Name', true)}
            {renderSelectField(
              'product_type',
              'Product Type',
              PRODUCT_TYPE_OPTIONS,
              true,
              'Select product...'
            )}
            {renderSelectField(
              'trial_term',
              'Trial Term',
              TRIAL_TERM_OPTIONS,
              false,
              'Select term...'
            )}
            {renderTextField('contact', 'Contact Name', true)}
            {renderTextField('contact_email', 'Contact Email', true, 'name@example.com')}
            {renderDateField('trial_start_date', 'Trial Start Date')}
            {renderDateField('trial_end_date', 'Trial End Date')}
            {renderTextareaField(
              'trial_notes',
              'Notes',
              false,
              'Any additional notes about this trial request...'
            )}
          </>
        )

      case 'Marketing Feedback / Suggestion':
        return (
          <>
            {renderTextField('requestor_name', 'Requestor Name', true)}
            {renderTextareaField(
              'feedback',
              'Feedback / Suggestion',
              true,
              'Share your marketing feedback or suggestion...'
            )}
          </>
        )

      case 'GPO Client Feedback':
        return (
          <>
            {renderTextField('summary', 'Summary', true, 'Brief summary of the client feedback')}
            {renderTextareaField(
              'issue_description',
              'Detailed description of the issue',
              true,
              'What went wrong? Describe the issue in detail.'
            )}
            {renderTextareaField(
              'workflow_description',
              'Detailed description of the workflow taken',
              true,
              'What steps did the client take that led to the issue?'
            )}
            {renderTextField(
              'editor_url',
              'Editor URL',
              false,
              'https://… (the URL while the file is in editor mode)'
            )}
            {renderPdfField('input_pdf', 'Input PDF', false, 'The PDF that was inputted')}
            {renderPdfField(
              'exported_pdf',
              'Exported PDF',
              false,
              'If the issue was with the export'
            )}
          </>
        )

      case 'Scan Request':
        return (
          <>
            {renderTextField('company_name', 'Company Name', true)}
            {renderTextField('domain_url', 'Domain URL', true, 'https://example.com')}
            {renderDateField('due_date', 'Due Date')}
            {renderTextareaField(
              'scan_notes',
              'Notes',
              false,
              'Any additional notes about this scan request...'
            )}
          </>
        )

      case 'Trial Scan':
        return (
          <>
            {renderUserSelectField('sales_rep', 'Sales Rep', true)}
            {renderTextField('company_name', 'Company Name', true)}
            {renderTextField('contact', 'Contact Name', true)}
            {renderTextField('contact_email', 'Contact Email', true, 'name@example.com')}
            {renderTextField('domain_url', 'Domain URL', true, 'https://example.com')}
            {renderSelectField(
              'trial_term',
              'Trial Term',
              TRIAL_TERM_OPTIONS,
              false,
              'Select term...'
            )}
            {renderDateField('trial_start_date', 'Trial Start Date')}
            {renderDateField('trial_end_date', 'Trial End Date')}
            {renderTextareaField(
              'scan_notes',
              'Notes',
              false,
              'Any additional notes about this trial scan...'
            )}
          </>
        )

      case 'Shipping Label':
        return (
          <>
            {renderTextField('recipient_name', 'Recipient Name', true)}
            {renderTextareaField(
              'shipping_address',
              'Shipping Address',
              true,
              'Full delivery address...'
            )}
            {renderTextField('package_weight', 'Package Weight', false, 'e.g. 2 kg')}
            {renderTextareaField(
              'notes',
              'Additional Notes',
              false,
              'Anything else we should know...'
            )}
          </>
        )

      default:
        return null
    }
  }

  return (
    <div className="space-y-6" role="main" aria-label="Internal support request form">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/tickets')}
          aria-label="Back to tickets"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Badge
          variant="outline"
          style={{
            backgroundColor: CATEGORICAL.plum.tint,
            color: CATEGORICAL.plum.solid,
            borderColor: CATEGORICAL.plum.solid,
          }}
        >
          <ClipboardList className="mr-1.5 h-3 w-3" aria-hidden="true" />
          Support
        </Badge>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Support Request</h1>
          <p className="text-sm text-muted-foreground mt-1">Submit an internal support request</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="max-w-2xl space-y-6">
          {/* Request Type Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Request Type</CardTitle>
              <CardDescription>
                Select the type of support request you need help with
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label htmlFor="requestType">Type of Request *</Label>
                <Select value={requestType} onValueChange={handleRequestTypeChange}>
                  <SelectTrigger
                    id="requestType"
                    aria-required="true"
                    aria-invalid={!!formErrors.requestType || undefined}
                    aria-describedby={formErrors.requestType ? 'requestType-error' : undefined}
                  >
                    <SelectValue placeholder="Select a request type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {REQUEST_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {renderFieldError('requestType')}
              </div>
            </CardContent>
          </Card>

          {/* Conditional Fields */}
          {requestType && (
            <Card>
              <CardHeader>
                <CardTitle>{requestType}</CardTitle>
                <CardDescription>Fill in the details for your request</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Optional account link — available for every request type */}
                <div className="space-y-2">
                  <Label htmlFor="related-account">Related Account (optional)</Label>
                  <RecordCombobox
                    id="related-account"
                    records={accounts}
                    value={accountId}
                    onValueChange={handleAccountChange}
                    placeholder="Link this request to an account..."
                    searchPlaceholder="Search accounts..."
                    emptyMessage="No accounts found."
                    displayField={(a) => a.name || ''}
                    selectedLabel={searchParams.get('company_name') || undefined}
                    aria-label="Related account"
                    onSearchChange={setAccountSearch}
                    isLoading={accountsLoading}
                    totalCount={accountsData?.total}
                  />
                </div>

                {renderConditionalFields()}
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          {requestType && (
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setRequestType('')
                  setFields({})
                  setFormErrors({})
                  setAttachments([])
                  setAccountId('')
                  setAccountSearch('')
                }}
              >
                Reset
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" aria-hidden="true" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}
